import React, { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import ToggleButton from "../../../../commons/ToggleButton";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useGetLeadDetailsQuery } from "../../../../../store/leadDetails";
import { useGetNotificationDetailsQuery } from "../../../../../store/professionalNotifications";
// import ModifyModal from "./ModifyModal";
import {
  useGetProfessionalInfoQuery,
  useSaveProfessionalInfoMutation,
} from "../../../../../store/professionalInfo";
import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  useGetUserDetailsQuery,
  useSaveAdminUserInfoMutation,
} from "../../../../../store/admin/professionalUserInfo";
import ModifyModal from "../../../../dashboard/dashboardSettings/userProfileDetail/ModifyModal";
import { adjustLeadLimits } from "../../../../../utilities/utils"; 

const ProfessionalCreditLimit = ({ isMobile }) => {
  const location = useLocation();
  const initialRender = useRef(null);
  const { professionalId } = location?.state ?? {};
  const [isToggleEnabled, setIsToggleEnabled] = useState(false);
  const [dailyLimit, setDailyLimit] = useState(null);
  const [weeklyLimit, setWeeklyLimit] = useState(null);
  const [dailyIsChange, setDailyIsChange] = useState(false);
  const [weeklyIsChange, setWeeklyIsChange] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { data: creditandLeadData, refetch: creditandLeadDataRefetch } =
    useGetLeadDetailsQuery();
  const { refetch: refetchNotificationsList } =
    useGetNotificationDetailsQuery();

  const {
    data: professionalInfoDetail,
    refetch: refetchProfessionalInfoDetail,
  } = useGetUserDetailsQuery(professionalId);

  const [
    saveProfessionalInfo,
    {
      isLoading: saveProfessionalInfoLoader,
      isSuccess: saveProfessionalInfoSuccess,
    },
  ] = useSaveAdminUserInfoMutation();

  useEffect(() => {
    if (saveProfessionalInfoSuccess && !saveProfessionalInfoLoader) {
      weeklyIsChange &&
        professionalInfoDetail?.data?.daily_cap == dailyLimit &&
        setWeeklyIsChange(false);
      dailyIsChange &&
        professionalInfoDetail?.data?.weekly_cap == weeklyLimit &&
        setDailyIsChange(false);
    }
  }, [saveProfessionalInfoSuccess]);

  useEffect(() => {
    if (professionalInfoDetail) {
      setIsToggleEnabled(!!professionalInfoDetail?.data?.credit_renew);
      initialRender.current = true;
      setDailyLimit(professionalInfoDetail?.data?.daily_cap);
      setWeeklyLimit(professionalInfoDetail?.data?.weekly_cap);
    }
  }, [professionalInfoDetail]);

  const handleToggle = () => {
    try {
      setIsToggleEnabled((prev) => !prev);
      const params = {
        _method: "PUT",
        credit_renew: !isToggleEnabled,
      };
      saveProfessionalInfo({ professionalId, ...params });
      toast.success("Your credit renewal settings have been updated.");
    } catch (error) {
      toast.error("Something went wrong!");
    }
  };

  const handleSuccess = () => {
    creditandLeadDataRefetch();
    refetchProfessionalInfoDetail();
    refetchNotificationsList();
  };

  const handleIsChange = (key, value) => {
    switch (key) {
      case "daily":
        setDailyIsChange(
          professionalInfoDetail?.data?.daily_cap != value &&
            !(value === "" && professionalInfoDetail?.data?.daily_cap == null)
        );
        break;
      case "weekly":
        setWeeklyIsChange(
          professionalInfoDetail?.data?.weekly_cap != value &&
            !(value === "" && professionalInfoDetail?.data?.weekly_cap == null)
        );
        break;
    }
  };

  const handleInputChange = (e, key) => {
    switch (key) {
      case "daily":
        setDailyLimit(e.target.value);
        handleIsChange("daily", e.target.value);
        break;
      case "weekly":
        handleIsChange("weekly", e.target.value);
        setWeeklyLimit(e.target.value);
        break;
    }
  };
  
  const handleUpdate = (key) => {
    if (adjustLeadLimits(dailyLimit, weeklyLimit)) {
      toast.error(
        "Your weekly lead limit cannot exceed 7 times (days in the week) your daily lead limit set. Please adjust either your daily or weekly lead limit accordingly."
      );
      setDailyLimit(professionalInfoDetail?.data?.daily_cap);
      setWeeklyLimit(professionalInfoDetail?.data?.weekly_cap);
      setWeeklyIsChange(false);
      setDailyIsChange(false)
      return;
    }
    try {
      const params = {
        _method: "PUT",
      };
      switch (key) {
        case "daily":
          params["daily_cap"] = dailyLimit;
          break;
        case "weekly":
          params["weekly_cap"] = weeklyLimit;
          break;
      }
      saveProfessionalInfo({ professionalId, ...params });
      toast.success("Your lead limits settings have been updated.");
    } catch (error) {
      toast.error("Something went wrong!");
    }
  };

  const handleCancel = (key) => {
    switch (key) {
      case "daily":
        setDailyLimit(professionalInfoDetail?.data?.daily_cap);
        setDailyIsChange(false);
        break;
      case "weekly":
        setWeeklyLimit(professionalInfoDetail?.data?.weekly_cap);
        setWeeklyIsChange(false);
        break;
    }
  };

  return (
    <div>
      {!isMobile && (
        <h4 className="text-[22px] font-bold leading-[160%] text-customGray mb-6">
          Credits, Lead Limits
        </h4>
      )}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-6 3xl:col-span-5 border border-solid border-[#EBEFF3] p-5 rounded-md bg-white">
          <div className="border-b border-solid border-[#EBEFF3] pb-[14px] mb-5 flex items-center justify-between">
            <div className="flex md:items-center gap-[6px]">
              <span className="mt-1 md:mt-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <g clipPath="url(#clip0_949_45261)">
                    <path
                      d="M9 14C9 15.657 11.686 17 15 17C18.314 17 21 15.657 21 14C21 12.343 18.314 11 15 11C11.686 11 9 12.343 9 14Z"
                      stroke="#1E90FE"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 14V18C9 19.656 11.686 21 15 21C18.314 21 21 19.656 21 18V14"
                      stroke="#1E90FE"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 6C3 7.072 4.144 8.062 6 8.598C7.856 9.134 10.144 9.134 12 8.598C13.856 8.062 15 7.072 15 6C15 4.928 13.856 3.938 12 3.402C10.144 2.866 7.856 2.866 6 3.402C4.144 3.938 3 4.928 3 6Z"
                      stroke="#1E90FE"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 6V16C3 16.888 3.772 17.45 5 18"
                      stroke="#1E90FE"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 11C3 11.888 3.772 12.45 5 13"
                      stroke="#1E90FE"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_949_45261">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              <h6 className="text-base font-bold leading-[160%] text-customGray md:flex items-center gap-[6px]">
                Current Credit Pack:{" "}
                <span className="text-[#ef7325] block">
                  {professionalInfoDetail?.data?.paymentOption?.name}
                </span>
              </h6>
            </div>
            <div>
              <Link
                className="text-sm font-bold leading-[160%] text-primary"
                onClick={() => setIsOpen(!isOpen)}
              >
                Modify Plan
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5 h-[42px] items-center bg-[#F1F4F6] rounded-[3px] px-[14px] py-[10px] border-b border-solid border-[#EBEFF3]">
            <span className="text-xs font-bold leading-[160%] uppercase text-customGray">
              Credit Balance:
            </span>
            <span className="text-sm font-black leading-[160%] uppercase text-customGray">
              {professionalInfoDetail?.data?.credits}
            </span>
          </div>
          {/* <div className="grid grid-cols-2 gap-5 py-[10px] items-center rounded-[3px] px-[14px] border-b border-solid border-[#EBEFF3]">
            <span className="text-xs font-black leading-[160%] uppercase text-customGray">
              Next Billing Date:
            </span>
            <span className="text-sm font-bold leading-[160%] uppercase text-[#485868]">
              05/05/2023,
            </span>
          </div> */}
          <div className="grid grid-cols-2 gap-5 py-[10px] items-center rounded-[3px] px-[14px]">
            <span className="text-xs font-black leading-[160%] uppercase text-customGray max-w-[150px]">
              Enable Automatic credit renewals
            </span>
            <ToggleButton isEnabled={isToggleEnabled} onToggle={handleToggle} />
          </div>
          <div className="py-[10px] items-center rounded-[3px] px-[14px]">
            <span className="text-sm font-normal leading-[160%] text-[#485868] max-w-[150px]">
              {
                "Your total credit balance is automatically topped off when your available credit balance falls below the minimum threshold for receiving leads."
              }
            </span>
          </div>
        </div>
        <div className="col-span-12 xl:col-span-6 3xl:col-span-7 border border-solid border-[#EBEFF3] p-5 rounded-md bg-white">
          <div className="border-b border-solid border-[#EBEFF3] pb-[14px] mb-5">
            <h6 className="text-base font-bold leading-[160%] text-customGray">
              Lead Limits
            </h6>
          </div>
          <p className="text-sm font-normal leading-[160%] text-[#485868] mb-5">
            Daily and weekly lead limits ensure our customers can handle their
            lead volume. Do not change without customer consent.
          </p>
          <div className="flex flex-col 3xl:flex-row 3xl:items-center gap-[14px] 3xl:gap-5 my-[14px] rounded-[3px] 3xl:px-[14px]">
            <span className="text-xs font-black leading-[160%] uppercase text-customGray w-[148px]">
              Daily Lead Limit:
            </span>
            <div className="flex items-center gap-3 max-w-[216px] w-full">
              <input
                className="text-sm font-bold leading-[160%]  text-customGray py-[10px] px-3 border border-solid border-[#EBEFF3] rounded-md max-w-[148px] w-full placeholder:font-normal bg-[#F1F4F6] focus:bg-[#FFFFFF]"
                placeholder="Not Sets"
                value={dailyLimit ?? ""}
                onClick={() => {
                  setWeeklyIsChange(false);
                  // setWeeklyLimit(professionalInfoDetail?.data?.weekly_cap);
                }}
                onChange={(e) => handleInputChange(e, "daily")}
              />

              {dailyIsChange && (
                <div className="flex items-center gap-2">
                  <CheckCircleIcon
                    className="w-6 h-6 text-[#16CA7C] cursor-pointer"
                    onClick={() => {
                      handleUpdate("daily");
                    }}
                  />
                  <XMarkIcon
                    className="w-6 h-6 text-[#EF2121] cursor-pointer"
                    onClick={() => {
                      handleCancel("daily");
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col 3xl:flex-row 3xl:items-center gap-[14px] 3xl:gap-5 rounded-[3px] 3xl:px-[14px]">
            <span className="text-xs font-black leading-[160%] uppercase text-customGray w-[148px]">
              Weekly Lead Limit:
            </span>
            <div className="flex items-center gap-3 max-w-[216px] w-full">
              <input
                className="text-sm font-bold leading-[140%] text-customGray py-[10px] px-3 border border-solid border-[#EBEFF3] rounded-md max-w-[148px] w-full bg-[#F1F4F6] focus:bg-[#FFFFFF] placeholder:font-normal"
                placeholder="Not Sets"
                value={weeklyLimit ?? ""}
                onClick={() => {
                  setDailyIsChange(false);
                  // setDailyLimit(professionalInfoDetail?.data?.daily_cap);
                }}
                onChange={(e) => handleInputChange(e, "weekly")}
              />
              {weeklyIsChange && (
                <div className="flex items-center gap-2">
                  <CheckCircleIcon
                    className="w-6 h-6 text-[#16CA7C] cursor-pointer"
                    onClick={() => {
                      handleUpdate("weekly");
                    }}
                  />
                  <XMarkIcon
                    className="w-6 h-6 text-[#EF2121] cursor-pointer"
                    onClick={() => {
                      handleCancel("weekly");
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ModifyModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        creditandLeadData={creditandLeadData}
        handleSuccess={handleSuccess}
        professionalInfoDetail={professionalInfoDetail?.data}
      />
    </div>
  );
};

export default ProfessionalCreditLimit;
